import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SCOPE_MUNICIPALITIES } from "@/config/scope";
import { fetcher } from "@/lib/api";

// Fotos representativas de cada concello (elegidas del zip cliente)
const HERO_PHOTOS = {
  cambados: {
    src: "/photos/cambados/palacio-de-fefinanes-7652.jpg",
    alt: "Cambados — Palacio de Fefiñanes",
  },
  meis: {
    src: "/photos/meis/plaza-de-espan-meis-16.jpg",
    alt: "Meis — Plaza de España",
  },
  "vilanova-de-arousa": {
    src: "/photos/vilanova/mirador-de-monte-lobeira.jpg",
    alt: "Vilanova de Arousa — Mirador de Monte Lobeira",
  },
};

function fmtInt(n) {
  return n == null
    ? "—"
    : new Intl.NumberFormat("es-ES").format(Math.round(n));
}

function fmtDec(n, d = 1) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n);
}

export default function ConcelloCards() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const reduced = useReducedMotion();
  const { data } = useSWR("/public/impacto/carbon-by-concello", fetcher, {
    revalidateOnFocus: false,
  });
  const byKey = {};
  (data?.items || []).forEach((it) => {
    byKey[it.key] = it;
  });

  return (
    <section
      id="concellos"
      className="bg-white py-24 lg:py-32 border-y border-salnes-stone/10"
      data-testid="landing-concellos-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-atlantic mb-4">
            {lang === "es" ? "Tres villas, una ría" : "Tres vilas, unha ría"}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-salnes-ink">
            {lang === "es"
              ? "Cambados, Meis y Vilanova de Arousa"
              : "Cambados, Meis e Vilanova de Arousa"}
          </h2>
          <p className="mt-4 text-salnes-stone text-lg max-w-xl leading-relaxed">
            {lang === "es"
              ? "Los tres municipios con recogida activa por la Mancomunidad do Salnés. Cada uno con su ritmo, sus datos y su territorio."
              : "Os tres concellos con recollida activa pola Mancomunidade do Salnés. Cada un co seu ritmo, os seus datos e o seu territorio."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SCOPE_MUNICIPALITIES.map((c, idx) => {
            const stats = byKey[c.key] || {};
            const photo = HERO_PHOTOS[c.key] || {};
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{
                  duration: 0.6,
                  delay: reduced ? 0 : idx * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`concello-card-${c.key}`}
              >
                <Link
                  to={`/impacto?concello=${c.key}`}
                  className="group block rounded-3xl overflow-hidden bg-salnes-ivory border border-salnes-stone/10 hover:shadow-2xl transition-shadow duration-500 h-full"
                >
                  {/* Photo */}
                  <div className="relative h-64 lg:h-72 overflow-hidden">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-salnes-ink/70 via-salnes-ink/10 to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                      <div>
                        <div className="font-serif text-white text-3xl leading-tight drop-shadow">
                          {c.name}
                        </div>
                        <div className="text-white/80 text-xs uppercase tracking-[0.22em] mt-1">
                          {fmtInt(c.population)} hab.
                        </div>
                      </div>
                      <div
                        className="w-3 h-3 rounded-full ring-2 ring-white/80"
                        style={{ background: c.color }}
                        aria-hidden
                      />
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="p-6 lg:p-8">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
                          {lang === "es" ? "Recogidos" : "Recollidos"}
                        </div>
                        <div className="font-serif text-3xl text-salnes-ink mt-1 tabular-nums">
                          {stats.t_total != null
                            ? fmtDec(stats.t_total, 1)
                            : "—"}
                          <span className="text-xs text-salnes-stone ml-1 font-sans">
                            t
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
                          CO₂e
                        </div>
                        <div className="font-serif text-3xl text-salnes-coral mt-1 tabular-nums">
                          {stats.co2e_kg != null
                            ? fmtInt(stats.co2e_kg)
                            : "—"}
                          <span className="text-xs text-salnes-stone ml-1 font-sans">
                            kg
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex items-center gap-2 text-salnes-atlantic text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                      {lang === "es" ? "Ver detalle" : "Ver detalle"}
                      <ArrowRight
                        size={14}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
